/**
 * Marketplace guide cluster (SEO).
 *
 * Editorial entry points for marketplace search intent. Each guide links into
 * the real product surfaces (/marketplace, /marketplace/explore,
 * /marketplace/sell, /how-it-works) and never duplicates the engine itself.
 */

export type MpGuide = {
  /** URL segment under /marketplace/guides/ */
  slug: string;
  h1: string;
  title: string;
  description: string;
  keywords: string[];
  intro: string;
  sections: { h2: string; body: string[] }[];
  faq: [string, string][];
  related: string[];
  /** Optional primary CTA target inside the product. */
  cta?: { to: string; key: "explore" | "sell" | "boards" | "howItWorks" };
};

export const MP_GUIDES: MpGuide[] = [
  {
    slug: "how-the-marketplace-works",
    h1: "How the BeHot.Now Marketplace works",
    title: "How the BeHot.Now Marketplace Works — Buy and Sell Creator Services",
    description:
      "A plain guide to the BeHot.Now Marketplace: publishing a service is free, orders are paid with Heat from your wallet, and every seller is verified before selling.",
    keywords: [
      "creator services marketplace",
      "how behot marketplace works",
      "buy creator services",
      "sell services online",
    ],
    intro:
      "The BeHot.Now Marketplace is where professionals publish paid services and buyers order them. It shares one account, one wallet and one notification system with the HOT boards, so the audience you build on a board can buy from you in the same place.",
    sections: [
      {
        h2: "Publishing is free, promotion is paid",
        body: [
          "Creating and publishing a service costs nothing. You build the listing in a step by step wizard: category and subcategory, overview and tags, packages and prices, media, then the requirements you need from a buyer.",
          "Heat is only involved when you want visibility: featuring a service in search and marketplace lists costs 10 Heat for 5 days or 25 Heat for 30 days, and putting a service on a public HOT board costs the board's minimum boost.",
        ],
      },
      {
        h2: "Every order runs through the wallet",
        body: [
          "Buyers never pay a seller by card inside the marketplace. Orders are paid with Heat from the Top-up wallet, and Reward Heat can be spent on marketplace orders too. If the balance is short, the checkout sends you to top up and completes the order automatically afterwards.",
          "The seller's share lands in the Earn wallet after the 15% platform fee, and is withdrawn to a verified bank account or a USDT/USDC ERC-20 address.",
        ],
      },
      {
        h2: "Moderation keeps the catalogue clean",
        body: [
          "New services and every edit go through moderation before they are publicly listed again. That is why editing a live service pauses selling until it is approved: the public catalogue always reflects what a moderator has seen.",
          "Sellers must complete identity verification (KYC) or business verification (KYB) before their first service goes live.",
        ],
      },
    ],
    faq: [
      ["Does it cost anything to publish a service?", "No. Publishing is free. You only pay Heat to feature a service or to put it on a HOT board."],
      ["How do buyers pay?", "With Heat from their wallet. Top-up Heat and Reward Heat can both be spent on marketplace orders."],
      ["When do I get paid?", "Your share moves to the Earn wallet when the order completes, after the 15% platform fee. Withdrawals are verified within 48 hours and processed in 5 to 8 working days."],
      ["Why did my service go back to moderation?", "Any edit to a live service resets it to pending moderation so the public catalogue stays accurate."],
    ],
    related: ["sell-services", "buy-services", "fees-and-pricing", "verification-kyc-kyb"],
    cta: { to: "/marketplace", key: "explore" },
  },
  {
    slug: "sell-services",
    h1: "How to sell services on BeHot.Now",
    title: "How to Sell Services on BeHot.Now — Free Listings, Heat Promotion",
    description:
      "Step by step guide to selling on the BeHot.Now Marketplace: verification, the service wizard, packages and prices, tags, media and getting your first orders.",
    keywords: [
      "sell services online",
      "freelance marketplace for creators",
      "sell music services",
      "publish a service listing",
    ],
    intro:
      "Selling on BeHot.Now takes one verification and one service listing. This guide walks the whole flow, from switching your account to professional mode to your first paid order.",
    sections: [
      {
        h2: "1. Switch to a professional profile",
        body: [
          "Every account can hold both a user dashboard and a professional dashboard. Activating the professional profile costs 99 Heat once and unlocks selling, your public professional page and marketplace analytics.",
          "You then complete KYC as an individual or KYB as a company. Name, address, phone and a valid document are mandatory; companies also submit the company name and trade licence.",
        ],
      },
      {
        h2: "2. Build the service in the wizard",
        body: [
          "The wizard has five steps: category and subcategory, overview with tags, packages and pricing, media, and buyer requirements. Suggestions adapt to the category you picked, so a DJ mix service gets different prompts than a brand identity service.",
          "Add up to ten preset tags, a cover image and gallery items (3 MB per file, with crop and zoom), and be explicit about delivery time and revisions in each package.",
        ],
      },
      {
        h2: "3. Get seen",
        body: [
          "A published service is discoverable in marketplace search and category pages for free. To move up, feature it for 5 days (10 Heat) or 30 days (25 Heat), or promote it onto a public HOT board where fans and clients can boost it.",
          "Services listed on a board get a marketplace badge, a link back to your professional profile and public click counts.",
        ],
      },
    ],
    faq: [
      ["Do I need a company to sell?", "No. Individuals complete KYC; companies complete KYB with a trade licence."],
      ["How many services can I publish?", "There is no fixed limit. Each one is moderated before it goes live."],
      ["Can I pause selling?", "Yes. Pause a service from the seller dashboard at any time; existing orders continue."],
      ["What does featuring actually do?", "It places the service higher in marketplace lists and search for the period you paid for: 5 days for 10 Heat or 30 days for 25 Heat."],
    ],
    related: ["how-the-marketplace-works", "fees-and-pricing", "promote-and-featuring", "verification-kyc-kyb"],
    cta: { to: "/marketplace/sell", key: "sell" },
  },
  {
    slug: "buy-services",
    h1: "How to buy services on BeHot.Now",
    title: "How to Buy Creator Services on BeHot.Now — Wallet Checkout Explained",
    description:
      "How buying works on the BeHot.Now Marketplace: browsing verified sellers, package choice, wallet checkout with Heat, delivery, revisions and dispute protection.",
    keywords: [
      "buy creator services",
      "hire a creator",
      "hire a musician online",
      "marketplace checkout",
    ],
    intro:
      "Buying on BeHot.Now means ordering a defined package from a verified professional and paying it from your wallet. Nothing is negotiated in the dark: scope, price, delivery time and revisions are written into the package before you pay.",
    sections: [
      {
        h2: "Find the right seller",
        body: [
          "Browse by category and subcategory, filter with tags, or start from the marketplace HOT list where sellers have put Heat behind their services. Every service page shows the seller's professional profile, rank signals, completed orders and public reviews.",
          "If you cannot find what you need, post a request and let sellers send you paid offers instead.",
        ],
      },
      {
        h2: "Pay from your wallet",
        body: [
          "Checkout uses Heat from your Top-up wallet, and Reward Heat earned from referrals can be spent here too. If your balance is short, the flow sends you to top up and returns to complete the order automatically.",
          "Every top-up is invoiced in AED with a sequential invoice number and a PDF copy sent to your email.",
        ],
      },
      {
        h2: "Delivery, revisions and disputes",
        body: [
          "You submit the requirements the seller asked for, the seller delivers inside the agreed window, and you accept or request the revisions included in the package.",
          "If something goes wrong, open a dispute from the order page. Support reviews the evidence from both sides and can release, split or refund the order.",
        ],
      },
    ],
    faq: [
      ["Can I pay by card directly?", "No. Marketplace orders are paid with Heat from your wallet; you top the wallet up by card."],
      ["Are sellers verified?", "Yes. Every seller passes KYC or KYB before publishing a service."],
      ["What if the delivery is not what I ordered?", "Request the revisions included in the package, and open a dispute from the order if it still does not match the scope."],
      ["Do I get an invoice?", "Yes. Every top-up generates a sequential PDF invoice in AED, emailed to you automatically."],
    ],
    related: ["how-the-marketplace-works", "orders-and-delivery", "disputes-and-refunds", "categories-and-tags"],
    cta: { to: "/marketplace/explore", key: "explore" },
  },
  {
    slug: "fees-and-pricing",
    h1: "Marketplace fees and pricing on BeHot.Now",
    title: "BeHot.Now Marketplace Fees — Free Listings, 15% Platform Fee",
    description:
      "Every marketplace cost in one place: free publishing, 99 Heat professional activation, 10 or 25 Heat featuring, board minimums and the 15% platform fee on completed orders.",
    keywords: [
      "marketplace fees",
      "freelance platform commission",
      "15% platform fee",
      "creator marketplace pricing",
    ],
    intro:
      "There is no subscription on BeHot.Now. You pay for two things only: visibility and completed sales. This page lists every marketplace cost exactly as it is charged.",
    sections: [
      {
        h2: "What costs Heat",
        body: [
          "Activating a professional profile costs 99 Heat once. Featuring a service costs 10 Heat for 5 days or 25 Heat for 30 days. Promoting a service onto a public HOT board costs that board's minimum boost, which is shown before you confirm.",
          "Publishing, editing, receiving messages, receiving offers and receiving orders cost nothing.",
        ],
      },
      {
        h2: "What costs a percentage",
        body: [
          "Completed orders carry a 15% platform fee. The fee is deducted before the balance is credited to your Earn wallet, so the amount you see in Earn is the amount you can withdraw.",
          "Withdrawals themselves are free from the platform side; your bank or the network may charge its own fee.",
        ],
      },
      {
        h2: "How Heat is bought",
        body: [
          "Heat is the platform credit and is bought as a one-off top-up, never as a subscription. Every top-up is invoiced in AED and a PDF copy is emailed to you and to finance.",
          "Reward Heat earned from referrals can be spent on boosts and on marketplace orders, but cannot be withdrawn.",
        ],
      },
    ],
    faq: [
      ["Is there a monthly fee?", "No. There is no subscription anywhere on BeHot.Now."],
      ["How much is the seller commission?", "15% of the order value, deducted before the balance reaches your Earn wallet."],
      ["Does featuring guarantee sales?", "No. It buys placement in marketplace lists and search for the period you paid for, not results."],
      ["Can referral Heat pay marketplace fees?", "Reward Heat can be spent on boosts and marketplace orders. It cannot be withdrawn as cash."],
    ],
    related: ["promote-and-featuring", "payouts-and-withdrawals", "sell-services", "how-the-marketplace-works"],
    cta: { to: "/how-it-works", key: "howItWorks" },
  },
  {
    slug: "promote-and-featuring",
    h1: "Featuring and promoting a marketplace service",
    title: "Feature a Service on BeHot.Now — 10 Heat for 5 Days, 25 for 30",
    description:
      "How paid featuring works in the BeHot.Now Marketplace: 5 days for 10 Heat, 30 days for 25 Heat, where featured services appear, and how HOT board promotion differs.",
    keywords: [
      "feature a service",
      "promote freelance listing",
      "marketplace advertising",
      "paid placement marketplace",
    ],
    intro:
      "Publishing is free, so visibility is the thing sellers actually compete for. BeHot.Now gives you two separate levers: marketplace featuring and HOT board promotion.",
    sections: [
      {
        h2: "Marketplace featuring",
        body: [
          "Featuring places your service above non-featured results in marketplace search, category pages and the marketplace home lists for a fixed period: 5 days for 10 Heat, or 30 days for 25 Heat.",
          "Featuring is bought per service, renews only when you choose, and never changes the service's price or scope.",
        ],
      },
      {
        h2: "HOT board promotion",
        body: [
          "A service can also be promoted onto a public HOT board. There it behaves like any other listing: it ranks by the Heat behind it, shows its click count and can be boosted by fans as well as by you.",
          "Board promotion reaches people who are browsing categories rather than searching the marketplace, and it links back to the service and to your professional profile.",
        ],
      },
      {
        h2: "Choosing between them",
        body: [
          "Featuring is best for buyer intent: people already searching for what you sell. Board promotion is best for discovery and reputation, because it puts you in front of a category audience and lets supporters push you up.",
          "Many sellers run both: featuring for the conversion window, board Heat for the long tail.",
        ],
      },
    ],
    faq: [
      ["Does featuring change my search ranking permanently?", "No. It applies only for the period you paid for."],
      ["Can I feature more than one service?", "Yes. Featuring is bought per service."],
      ["Can fans boost my service?", "Only when it is promoted onto a HOT board. Marketplace featuring is paid by the seller alone."],
      ["What happens when featuring expires?", "The service stays published and searchable; it simply loses the placement boost."],
    ],
    related: ["marketplace-boards", "fees-and-pricing", "sell-services", "how-the-marketplace-works"],
    cta: { to: "/marketplace/hot", key: "boards" },
  },
  {
    slug: "marketplace-boards",
    h1: "Marketplace services on the HOT boards",
    title: "Marketplace Services on HOT Boards — Heat Ranking for Sellers",
    description:
      "How a marketplace service becomes a HOT board listing on BeHot.Now: board minimums, owner boosts, fan boosts, click counts and the link back to the seller profile.",
    keywords: [
      "rank a service",
      "heat ranking sellers",
      "paid leaderboard services",
      "boost a service listing",
    ],
    intro:
      "The HOT boards and the Marketplace are one system. A service can leave the catalogue and compete on a public board, where the ranking rule is the Heat behind it and nothing else.",
    sections: [
      {
        h2: "Getting a service onto a board",
        body: [
          "From the seller dashboard, promote the service to a board. You pay that board's minimum entry, the listing goes through moderation, and once approved it enters the ranking like any other listing.",
          "The listing carries a marketplace badge, the seller's rank signals and a direct link to both the service page and the professional profile.",
        ],
      },
      {
        h2: "How the ranking moves",
        body: [
          "Owners boost at the position price for the spot they want. Anyone else — fans, clients, a community — can place a free bid from 3 Heat upwards on the same listing, and that Heat counts toward the total.",
          "Positions update immediately, so a board can reorder within seconds of a boost.",
        ],
      },
      {
        h2: "What buyers see",
        body: [
          "Every board listing publicly shows its total clicks and links out to the real destination. For a service that destination is the service page, so board traffic converts inside the marketplace.",
          "Boards reset on a monthly refresh cycle, which keeps positions contestable rather than permanently owned.",
        ],
      },
    ],
    faq: [
      ["Is board promotion the same as featuring?", "No. Featuring is placement inside marketplace lists; board promotion is a ranked listing on a public HOT board."],
      ["Can buyers boost my service?", "Yes. Non-owners can place a free bid from 3 Heat on any board listing."],
      ["What does it cost to enter a board?", "The board's current minimum, shown before you confirm. It depends on the Heat already on that board."],
      ["Do board listings expire?", "Boosts have a duration, and boards are refreshed monthly, so positions must be defended."],
    ],
    related: ["promote-and-featuring", "how-the-marketplace-works", "fees-and-pricing", "buy-services"],
    cta: { to: "/marketplace/hot", key: "boards" },
  },
  {
    slug: "verification-kyc-kyb",
    h1: "Seller verification: KYC and KYB",
    title: "Seller Verification on BeHot.Now — KYC and KYB Requirements",
    description:
      "What BeHot.Now requires to verify a seller: mandatory name, address, phone and document for KYC, company name and trade licence for KYB, plus how re-submission works.",
    keywords: [
      "marketplace kyc",
      "seller verification",
      "kyb trade licence",
      "verified freelancer marketplace",
    ],
    intro:
      "Every seller is verified before a service goes live. Verification protects buyers, keeps payouts compliant and is the reason marketplace listings can be trusted.",
    sections: [
      {
        h2: "Individual verification (KYC)",
        body: [
          "Full legal name, residential address, phone number and a valid identity document are all mandatory. Uploads are limited to 3 MB per file.",
          "Submissions are reviewed by the compliance team. You receive a platform notification and an email at every status change.",
        ],
      },
      {
        h2: "Business verification (KYB)",
        body: [
          "Companies submit the registered company name, trade licence and the business details needed for invoicing, in addition to the details of the person acting for the company.",
          "A verified business profile sells under the company name and receives payouts to the company account.",
        ],
      },
      {
        h2: "When more information is requested",
        body: [
          "If a reviewer needs something else, the request arrives as a notification that links straight to the verification page, where you can add the missing item and resubmit.",
          "Nothing is lost while a request is open: your drafts stay in place and your services simply wait for approval.",
        ],
      },
    ],
    faq: [
      ["How long does verification take?", "Most reviews are handled within a working day. You are notified at each status change."],
      ["What is the maximum upload size?", "3 MB per document."],
      ["Can I sell while verification is pending?", "No. Services go live once verification is approved."],
      ["Is my document visible to buyers?", "No. Verification documents are only accessible to the compliance team."],
    ],
    related: ["sell-services", "payouts-and-withdrawals", "how-the-marketplace-works", "disputes-and-refunds"],
    cta: { to: "/marketplace/sell", key: "sell" },
  },
  {
    slug: "payouts-and-withdrawals",
    h1: "Payouts and withdrawals for sellers",
    title: "BeHot.Now Seller Payouts — Earn Wallet, 48h Review, Bank or USDC",
    description:
      "How sellers get paid on BeHot.Now: the Earn wallet after the 15% fee, 48 hour verification, 5 to 8 working days processing, bank transfer in EUR, USD or AED, or USDT/USDC ERC-20.",
    keywords: [
      "creator payouts",
      "freelance withdrawal",
      "usdc payout",
      "marketplace earn wallet",
    ],
    intro:
      "Marketplace earnings live in a dedicated Earn wallet, separate from your Top-up and Reward balances, and are withdrawn to a payout method you verify once.",
    sections: [
      {
        h2: "Three wallets, one balance each",
        body: [
          "Top-up Heat pays for services, listings and featuring. Reward Heat from referrals boosts listings and pays marketplace orders. Earn is the only withdrawable balance.",
          "Keeping them separate means promotional credit can never be cashed out and real earnings are never spent by accident.",
        ],
      },
      {
        h2: "Setting up a payout method",
        body: [
          "Payout details live in your profile: a bank account with IBAN, BIC/SWIFT and the holder details, or a USDT/USDC ERC-20 address. Editing or saving payout details requires an email OTP.",
          "Withdrawals are only sent to a verified method that matches your verified identity or company.",
        ],
      },
      {
        h2: "Timing",
        body: [
          "A withdrawal request is verified and approved within 48 hours. Once approved, processing takes 5 to 8 working days depending on the rail.",
          "The minimum withdrawal is US$100. Every movement appears in your transactions page and can be exported as PDF.",
        ],
      },
    ],
    faq: [
      ["What is the minimum withdrawal?", "US$100."],
      ["How long does a payout take?", "48 hours for verification and approval, then 5 to 8 working days to process."],
      ["Which currencies are supported?", "Bank transfer in EUR, USD or AED, or USDT/USDC on ERC-20."],
      ["Why does editing payout details ask for a code?", "Payout data is OTP protected so nobody can redirect your earnings from a hijacked session."],
    ],
    related: ["fees-and-pricing", "verification-kyc-kyb", "orders-and-delivery", "sell-services"],
    cta: { to: "/how-it-works", key: "howItWorks" },
  },
  {
    slug: "orders-and-delivery",
    h1: "Orders, delivery and revisions",
    title: "Marketplace Orders on BeHot.Now — Requirements, Delivery, Revisions",
    description:
      "The full order lifecycle on the BeHot.Now Marketplace: requirements, delivery windows, revisions, acceptance, automatic completion and where notifications appear.",
    keywords: [
      "marketplace order flow",
      "service delivery revisions",
      "freelance order process",
      "order requirements",
    ],
    intro:
      "An order is a contract with a defined scope. Both sides see the same package terms, the same clock and the same message thread, so there is nothing to argue about later.",
    sections: [
      {
        h2: "Before the work starts",
        body: [
          "The buyer pays from the wallet and submits the requirements the seller defined in the wizard. The delivery clock starts when the requirements are complete.",
          "Everything about the order — files, briefs, questions — stays in the order thread, which is also where real-time messages and notifications land.",
        ],
      },
      {
        h2: "Delivery and revisions",
        body: [
          "The seller delivers inside the package window. The buyer accepts, or requests one of the revisions included in the package.",
          "Extra work beyond the package scope is handled as a new order or a custom offer, so the original terms stay intact.",
        ],
      },
      {
        h2: "Completion",
        body: [
          "On acceptance the order completes, the platform fee is applied and the seller's share moves to the Earn wallet.",
          "Both parties are notified at every status change, in the notification centre and by email.",
        ],
      },
    ],
    faq: [
      ["What if the buyer never answers?", "Orders complete automatically after the acceptance window so sellers are not blocked indefinitely."],
      ["Can I change the price after ordering?", "No. Extra scope is handled with a new order or a custom offer."],
      ["Where do I see order updates?", "In the order page, the notification centre and your email."],
      ["Are messages real time?", "Yes. The messenger is live and unread counts appear in the header."],
    ],
    related: ["buy-services", "disputes-and-refunds", "payouts-and-withdrawals", "how-the-marketplace-works"],
    cta: { to: "/marketplace/orders", key: "explore" },
  },
  {
    slug: "disputes-and-refunds",
    h1: "Disputes and refunds",
    title: "Marketplace Disputes and Refunds on BeHot.Now — How Review Works",
    description:
      "How BeHot.Now handles marketplace disputes: opening a case from the order, the evidence both sides provide, possible outcomes and how refunds return to the wallet.",
    keywords: [
      "marketplace dispute",
      "freelance refund policy",
      "order protection",
      "buyer protection marketplace",
    ],
    intro:
      "Most orders never need this page. When one does, the process is deliberately simple: the order scope is the reference, both sides submit evidence, and support decides.",
    sections: [
      {
        h2: "Opening a dispute",
        body: [
          "A dispute is opened from the order page itself, which attaches the package terms, the requirements and the full message history automatically.",
          "The order is frozen while the case is open: no funds move to the Earn wallet until it is resolved.",
        ],
      },
      {
        h2: "Possible outcomes",
        body: [
          "Support can release the order to the seller, split it, or refund the buyer in full. Refunds return as Heat to the wallet the order was paid from.",
          "Repeated abuse on either side affects account standing and can end selling privileges.",
        ],
      },
      {
        h2: "How to avoid disputes",
        body: [
          "Sellers: write the package scope, delivery time and revision count precisely, and keep every promise in the order thread rather than in private channels.",
          "Buyers: submit complete requirements at the start and use the included revisions instead of asking for new scope.",
        ],
      },
    ],
    faq: [
      ["Where does a refund go?", "Back to the wallet the order was paid from, as Heat."],
      ["Does opening a dispute pause the payout?", "Yes. Funds stay frozen until the case is resolved."],
      ["Can I dispute after accepting?", "Acceptance closes the order. Contact support if something surfaces afterwards."],
      ["Who decides?", "The BeHot.Now support team, using the order scope and the evidence from both sides."],
    ],
    related: ["orders-and-delivery", "buy-services", "verification-kyc-kyb", "fees-and-pricing"],
    cta: { to: "/marketplace/orders", key: "explore" },
  },
  {
    slug: "categories-and-tags",
    h1: "Marketplace categories, subcategories and tags",
    title: "Marketplace Categories and Tags on BeHot.Now — Get Found by Buyers",
    description:
      "How the BeHot.Now Marketplace taxonomy works: main categories, deep subcategories, a general option in every branch, and up to ten preset tags per service.",
    keywords: [
      "marketplace categories",
      "service tags",
      "freelance categories",
      "get found by buyers",
    ],
    intro:
      "Buyers navigate by category first and refine with tags. Choosing the right branch is the cheapest ranking decision you will make, because it decides which searches you are eligible for at all.",
    sections: [
      {
        h2: "Category, then subcategory",
        body: [
          "You pick a main category and then a subcategory from a dropdown. Every branch has a general option, so nothing gets forced into the wrong slot.",
          "The wizard's suggestions for titles, packages and requirements change with the branch you choose, which keeps listings specific instead of generic.",
        ],
      },
      {
        h2: "Tags do the fine matching",
        body: [
          "Up to ten tags come from a curated preset list, so buyers filtering by a tag always land on comparable services. Free-text keyword stuffing is not possible by design.",
          "Pick tags that describe deliverables and tools, not adjectives.",
        ],
      },
      {
        h2: "Category pages are indexable",
        body: [
          "Each marketplace category page is a public, crawlable page with its own metadata, so services inside it can be found from search engines and not only from inside the app.",
          "That is also why moderation matters: a clean catalogue keeps the category pages worth ranking.",
        ],
      },
    ],
    faq: [
      ["How many tags can I use?", "Up to ten, from the preset list."],
      ["What if my service fits two categories?", "Publish it where the buyer would look first; you can publish a second, differently scoped service elsewhere."],
      ["Are category pages public?", "Yes. Marketplace category pages are crawlable and have their own metadata."],
      ["Can I invent my own tag?", "No. Tags are curated so filtering stays reliable."],
    ],
    related: ["sell-services", "buy-services", "promote-and-featuring", "how-the-marketplace-works"],
    cta: { to: "/marketplace/categories", key: "explore" },
  },
  {
    slug: "music-and-creator-services",
    h1: "Music and creator services on BeHot.Now",
    title: "Sell Music and Creator Services on BeHot.Now — Mixing, DJ, Design",
    description:
      "Sell and buy music and creator services on BeHot.Now: mixing and mastering, DJ mixes, production, vocals, artwork, video, social content and playlist pitching support.",
    keywords: [
      "sell music services",
      "hire a music producer",
      "mixing and mastering marketplace",
      "dj services online",
    ],
    intro:
      "BeHot.Now started with music and creator audiences, and the marketplace is built for them: the same account holds your public artist page, your board listings and the services you sell.",
    sections: [
      {
        h2: "What sells here",
        body: [
          "Mixing and mastering, production and beats, topline and vocals, DJ mixes and edits, artwork and cover design, video and visualisers, social content, and campaign support such as playlist pitching preparation.",
          "Branding, ads and app or startup services have their own branches, so a music seller is never buried under unrelated categories.",
        ],
      },
      {
        h2: "Your artist page is part of the funnel",
        body: [
          "Your public profile carries your connected socials, your listings and your services in one place, with a shareable link that also carries your referral code.",
          "Fans arriving from a board or a shared link can hire you without leaving the platform.",
        ],
      },
      {
        h2: "Promotion belongs next to selling",
        body: [
          "Promote covers playlist pitching to curators, influencer collaborations and simplified ad campaigns; the marketplace covers the work itself. Both are paid with the same Heat wallet.",
          "For visibility on the music boards specifically, promote a service onto the music board and let fans boost it.",
        ],
      },
    ],
    faq: [
      ["Can I sell if I already have a listing on a board?", "Yes. Boards and the marketplace share one account and one wallet."],
      ["Is playlist pitching part of the marketplace?", "Pitching campaigns live under Promote; sellers can offer preparation and assets as marketplace services."],
      ["Do I need a professional profile to sell music services?", "Yes, professional activation costs 99 Heat once."],
      ["Can fans pay me directly?", "Fans can boost your listings and buy your services; earnings land in the Earn wallet."],
    ],
    related: ["sell-services", "marketplace-boards", "promote-and-featuring", "fees-and-pricing"],
    cta: { to: "/marketplace/explore", key: "explore" },
  },
];

export const MP_GUIDE_SLUGS = MP_GUIDES.map((g) => g.slug);

export function getMpGuide(slug: string): MpGuide | undefined {
  return MP_GUIDES.find((g) => g.slug === slug);
}
